// Shared enums + labels for the Stroller Quiz Attributes system.
// Powers the admin editor, the comparison tool, and the quiz UI.

export const OPTS = {
  seats: [[1, "1"], [2, "2"], [3, "3"], [4, "4"]],
  stroller_type: [["single", "Single"], ["double", "Double"], ["triple", "Triple"], ["quad", "Quad"]],
  seating_style: [["single", "Single"], ["side_by_side", "Side-by-side"], ["tandem", "Tandem"], ["other", "Other"]],
  weight_category: [["ultra_light", "Ultra lightweight"], ["light", "Lightweight"], ["medium", "Medium"], ["heavy_duty", "Heavy-duty"]],
  fold_difficulty: [["very_easy", "Very easy"], ["easy", "Easy"], ["moderate", "Moderate"], ["difficult", "Difficult"]],
  comfort: [["basic", "Basic"], ["good", "Good"], ["very", "Very comfortable"], ["premium", "Premium"]],
  seat_padding: [["light", "Light"], ["medium", "Medium"], ["plush", "Plush"]],
  recline: [["upright", "Upright only"], ["partial", "Partial recline"], ["deep", "Deep recline"], ["full", "Near-flat / full recline"]],
  nap_friendly: [["yes", "Yes"], ["somewhat", "Somewhat"], ["no", "No"]],
  canopy_size: [["small", "Small"], ["medium", "Medium"], ["large", "Large"], ["xl", "Extra large"]],
  basket_size: [["small", "Small"], ["medium", "Medium"], ["large", "Large"], ["xl", "Extra large"]],
  best_terrain: [["smooth", "Smooth pavement"], ["theme_parks", "Theme parks"], ["mixed", "Mixed pavement"], ["rough", "Rough pavement"], ["all_terrain", "All-terrain"]],
  ride_quality: [["basic", "Basic"], ["smooth", "Smooth"], ["very_smooth", "Very smooth"]],
  suspension: [["none", "None / minimal"], ["standard", "Standard"], ["advanced", "Advanced"]],
  maneuverability: [["good", "Good"], ["very_good", "Very good"], ["excellent", "Excellent"]],
  park_suitability: [["good", "Good"], ["very_good", "Very good"], ["excellent", "Excellent"]],
};

export const SPECIAL_FEATURES = [
  ["lightweight", "Lightweight"],
  ["easy_fold", "Easy fold"],
  ["compact", "Compact"],
  ["best_for_naps", "Best for naps"],
  ["best_for_older_children", "Best for older children"],
  ["best_for_toddlers", "Best for toddlers"],
  ["best_for_infants", "Best for infants"],
  ["best_for_tall_children", "Best for tall children"],
  ["best_for_heavier_children", "Best for heavier children"],
  ["best_for_disney", "Best for Disney"],
  ["best_for_universal", "Best for Universal"],
  ["best_for_all_day_park_use", "Best for all-day park use"],
  ["best_for_rough_terrain", "Best for rough terrain"],
  ["best_for_storage", "Best for storage"],
  ["best_for_comfort", "Best for comfort"],
  ["best_for_travel", "Best for travel"],
  ["best_premium", "Best premium stroller"],
];

export const attrLabel = (field, value) => {
  const list = OPTS[field];
  if (!list) return value ?? "—";
  const hit = list.find(([v]) => v === value);
  return hit ? hit[1] : "—";
};

export const featureLabel = (v) => (SPECIAL_FEATURES.find(([k]) => k === v)?.[1]) || v;

// 1..N rating position for "dot" style comparison rows.
export const ratingOf = (field, value) => {
  const list = OPTS[field];
  if (!list) return 0;
  const idx = list.findIndex(([v]) => v === value);
  return idx < 0 ? 0 : idx + 1;
};

// Disney stroller size policy: max 31" wide × 52" long. `unfolded_size` is
// stored as "L × W × H" — returns true/false when parseable, null otherwise.
export const disneyApproved = (a = {}) => {
  const s = a?.unfolded_size || "";
  const nums = (s.match(/\d+(\.\d+)?/g) || []).map(Number);
  if (nums.length < 2) return null;
  return nums[0] <= 52 && nums[1] <= 31;
};

export const fmtAge = (a = {}) => {
  const min = Number(a.min_age_months);
  const max = Number(a.max_age_years);
  if (!min && !max) return "—";
  const lo = min >= 12 ? `${Math.round(min / 12)} yr` : `${min || 0} mo`;
  return max ? `${lo} – ${max} yrs` : `${lo}+`;
};

// Comparison table rows — [label, render descriptor]
// type: lbs | seats | age | enum | rate (dots) | bool | text | features
export const COMPARE_ROWS = [
  { label: "Stroller weight", type: "lbs", key: "weight_lbs" },
  { label: "Weight category", type: "enum", key: "weight_category" },
  { label: "Unfolded size", type: "size", key: "unfolded_size" },
  { label: "Children supported", type: "seats", key: "seats" },
  { label: "Recommended ages", type: "age" },
  { label: "Max weight per seat", type: "lbs", key: "max_child_weight_lbs" },
  { label: "Seating style", type: "enum", key: "seating_style" },
  { label: "Recline", type: "enum", key: "recline" },
  { label: "Nap friendly", type: "enum", key: "nap_friendly" },
  { label: "Child comfort", type: "rate", key: "comfort" },
  { label: "Seat padding", type: "rate", key: "seat_padding" },
  { label: "Canopy size", type: "rate", key: "canopy_size" },
  { label: "Storage basket", type: "rate", key: "basket_size" },
  { label: "Fold difficulty", type: "enum", key: "fold_difficulty" },
  { label: "One-hand fold", type: "bool", key: "one_hand_fold" },
  { label: "Compact fold", type: "bool", key: "compact_fold" },
  { label: "Folded size", type: "text", key: "folded_size" },
  { label: "Maneuverability", type: "rate", key: "maneuverability" },
  { label: "Suspension", type: "rate", key: "suspension" },
  { label: "Best terrain", type: "enum", key: "best_terrain" },
  { label: "Disney / park suitability", type: "rate", key: "park_suitability" },
  { label: "Easy on/off transportation", type: "bool", key: "transport_friendly" },
  { label: "Car-seat compatibility", type: "text", key: "car_seat_compat" },
  { label: "Standout features", type: "features" },
];
